import React, {useState, useEffect, useCallback, useContext} from "react";
import {NAV_SECTIONS, filterNavByRole} from "./config/navbar.config";
import UserLoginContext from "../../context/userLoginContext";
import {findParentOfActivePath} from "./action/navbar.action";
import NavigationMenuView from "./navbar.view";
import "../../styles/component/navbar/navbar.scss";

interface NavigationMenuProps {
    activePath: string;
    onNavigate: (path: string) => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}


function NavigationMenu({activePath, onNavigate, mobileOpen = false, onMobileClose}: NavigationMenuProps) {
    // const [openParents, setOpenParents] = useState<Set<string>>(new Set());
    const {user} = useContext(UserLoginContext);

    const role = user.roles[0];

    const sections = filterNavByRole(NAV_SECTIONS, role);

    // Initialize openParents - open parent of active child on first render
    const [openParents, setOpenParents] = useState<Set<string>>(() => {
        const activeParentId = findParentOfActivePath(sections, activePath, role);
        return activeParentId ? new Set([activeParentId]) : new Set();
    });

    const getNavCollapsed = () => {
        return JSON.parse(localStorage.getItem('nav_collapsed') ?? 'false');
    };

    const [collapsed, setCollapsed] = useState<boolean>(getNavCollapsed);


    function saveNavCollapsed(val:boolean){
        setCollapsed(val);
        localStorage.setItem('nav_collapsed',JSON.stringify(val));
    }

    // Close all parent items when collapsing
    useEffect(() => {
        if (collapsed) {
            setOpenParents(new Set());
        }
    }, [collapsed]);

    const toggleParent = useCallback((id: string) => {
        setOpenParents(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    }, []);

    const handleNavItemClick = useCallback((path: string) => {
        // Auto-expand sidebar when clicking nav item while collapsed
        if (collapsed) {
            saveNavCollapsed(false);
            // After expanding, check if the clicked path is a child and open its parent
            setTimeout(() => {
                const activeParentId = findParentOfActivePath(sections, path, role);
                if (activeParentId) {
                    setOpenParents(new Set([activeParentId]));
                }
            }, 50);
        }
        onNavigate(path);
        onMobileClose?.();
    }, [collapsed, onNavigate, onMobileClose, sections, role]);

    const handleParentClick = useCallback((id: string) => {
        // Auto-expand sidebar when clicking parent item while collapsed
        if (collapsed) {
            saveNavCollapsed(false);
            // Open the clicked parent after expanding
            setTimeout(() => {
                setOpenParents(new Set([id]));
            }, 50);
        } else {
            toggleParent(id);
        }
    }, [collapsed, toggleParent]);

    const handleCollapseToggle = useCallback(() => {
        if (collapsed) {
            // Expanding - check if active path is a child and open its parent
            saveNavCollapsed(false);
            const activeParentId = findParentOfActivePath(sections, activePath, role);
            if (activeParentId) {
                setTimeout(() => {
                    setOpenParents(new Set([activeParentId]));
                }, 50);
            }
        } else {
            // Collapsing - just collapse
            saveNavCollapsed(true);
        }
    }, [collapsed, sections, activePath, role]);



    return (
        <NavigationMenuView
            role={role}
            activePath={activePath}
            collapsed={collapsed}
            sections={sections}
            openParents={openParents}
            mobileOpen={mobileOpen}
            handleNavItemClick={handleNavItemClick}
            handleParentClick={handleParentClick}
            handleCollapseToggle={handleCollapseToggle}
        />
    );
}

export default NavigationMenu;
